import React, { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../../services/api';
import { Search } from 'lucide-react';
import AdminSubscriptionPanel from './AdminSubscriptionPanel';
import AdminLimitOverridesPanel from './AdminLimitOverridesPanel';
import AdminWorkspacesPanel from './AdminWorkspacesPanel';
import AdminProvisionPanel from './AdminProvisionPanel';
import DeleteAccountModal from '../../components/admin/DeleteAccountModal';
import ArchiveAccountModal from '../../components/admin/ArchiveAccountModal';

const AdminBilling = () => {
    const [accounts, setAccounts] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [creditAmount, setCreditAmount] = useState('');
    const [creditDescription, setCreditDescription] = useState('');
    const [creditAction, setCreditAction] = useState('add');
    const [submitting, setSubmitting] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });
    const [transactions, setTransactions] = useState([]);
    const [txnLoading, setTxnLoading] = useState(false);
    // Restore is triggered from the row directly (it deliberately does not open
    // the manage panel), so its error can't rely on the panel-scoped `msg`
    // banner below — that only renders when an account is selected.
    const [restoreError, setRestoreError] = useState('');

    const panelRef = useRef(null);

    useEffect(() => { fetchAccounts(); }, [showArchived]);

    // The manage panel renders below the full accounts table, so without this
    // clicking Manage looks like a no-op once the list is longer than the viewport.
    useEffect(() => {
        if (selectedAccount?.id) panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [selectedAccount?.id]);

    const fetchAccounts = async () => {
        try {
            const res = await adminAPI.getBillingAccounts({ archived: showArchived });
            const data = res.data || [];
            setAccounts(data);
            // Update selectedAccount with fresh data
            if (selectedAccount) {
                const updated = data.find(a => a.id === selectedAccount.id);
                if (updated) setSelectedAccount(updated);
            }
        } catch (err) {
            console.error('Failed to fetch billing accounts:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchTransactions = async (accountId) => {
        setTxnLoading(true);
        try {
            const res = await adminAPI.getAccountTransactions(accountId, { per_page: 20 });
            setTransactions(res.data || []);
        } catch (err) {
            console.error('Failed to fetch transactions:', err);
        } finally {
            setTxnLoading(false);
        }
    };

    const handleSelect = (account) => {
        if (selectedAccount?.id === account.id) {
            setSelectedAccount(null);
            setTransactions([]);
        } else {
            setSelectedAccount(account);
            fetchTransactions(account.id);
        }
        setMsg({ type: '', text: '' });
    };

    const handleCreditAction = async (e) => {
        e.preventDefault();
        console.log('[AdminBilling] handleCreditAction fired', { selectedAccount: selectedAccount?.id, creditAmount, creditAction });
        if (!selectedAccount) { setMsg({ type: 'error', text: 'No account selected' }); return; }
        if (!creditAmount) { setMsg({ type: 'error', text: 'Please enter an amount' }); return; }
        const amount = parseFloat(creditAmount);
        if (isNaN(amount) || amount <= 0) {
            setMsg({ type: 'error', text: 'Amount must be a positive number' });
            return;
        }

        setSubmitting(true);
        setMsg({ type: '', text: '' });
        try {
            if (creditAction === 'add') {
                const res = await adminAPI.addCredits(selectedAccount.id, amount, creditDescription || 'Manual credit by admin');
                setMsg({ type: 'success', text: res?.status?.message || `${amount} credits added` });
            } else {
                const res = await adminAPI.removeCredits(selectedAccount.id, amount, creditDescription || 'Manual deduction by admin');
                setMsg({ type: 'success', text: res?.status?.message || `${amount} credits removed` });
            }
            setCreditAmount('');
            setCreditDescription('');
            fetchAccounts();
            fetchTransactions(selectedAccount.id);
        } catch (err) {
            console.error('Credit operation failed:', err);
            const errorMsg = err.response?.data?.error || err.response?.data?.status?.message || err.message || 'Operation failed';
            setMsg({ type: 'error', text: errorMsg });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRestore = async (account) => {
        setRestoreError('');
        try {
            await adminAPI.restoreBillingAccount(account.id);
            // Mirrors DeleteAccountModal's onDeleted: restoring drops the account
            // out of the archived-only view, so a stale selection would keep the
            // Manage Credits panel (and its live credit add/remove form) open
            // against a row that no longer appears in the table.
            if (selectedAccount?.id === account.id) setSelectedAccount(null);
            fetchAccounts();
        } catch (err) {
            setRestoreError(err?.response?.data?.message || 'Could not restore this account.');
        }
    };

    const filtered = accounts.filter(a =>
        a.owner.name.toLowerCase().includes(search.toLowerCase()) ||
        a.owner.email.toLowerCase().includes(search.toLowerCase()) ||
        a.clients.some(c => c.name.toLowerCase().includes(search.toLowerCase()))
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-foreground">Billing Accounts</h1>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            className="rounded border-input"
                        />
                        Show archived
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by name, email, client..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 pr-4 py-2 border border-input rounded-lg text-sm focus:ring-black focus:border-black w-72"
                        />
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    </div>
                </div>
            </div>

            {restoreError && (
                <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
                    {restoreError}
                </div>
            )}

            {/* Manual provisioning */}
            <AdminProvisionPanel accounts={accounts} onChange={fetchAccounts} />

            {/* Accounts Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Owner</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Clients</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Balance</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                            <th className="px-6 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">No accounts found</td></tr>
                        ) : filtered.map((account) => (
                            <tr key={account.id} className={`cursor-pointer transition-colors ${selectedAccount?.id === account.id ? 'bg-indigo-50' : 'hover:bg-muted/50'}`} onClick={() => handleSelect(account)}>
                                <td className="px-4 py-3">
                                    <div className="text-sm font-medium text-foreground">{account.owner.name}</div>
                                    <div className="text-sm text-muted-foreground">{account.owner.email}</div>
                                </td>
                                <td className="px-4 py-3 text-sm text-foreground">
                                    {account.clients.map(c => c.name).join(', ') || '—'}
                                </td>
                                <td className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                                    {Number(account.total_balance ?? account.balance ?? 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${account.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {account.status}
                                        </span>
                                        {/* Null for almost every account. Non-null means a
                                            purge is in flight or has failed — without this a
                                            failed purge leaves the row looking entirely normal. */}
                                        {account.purge_state && (
                                            <span
                                                title={`Purge ${account.purge_state}`}
                                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    account.purge_state === 'failed'
                                                        ? 'bg-destructive/10 text-destructive'
                                                        : 'bg-muted text-muted-foreground'
                                                }`}
                                            >
                                                purge {account.purge_state}
                                            </span>
                                        )}
                                        {account.archived_at && (
                                            /* archive_reason is collected by the modal, stored, and
                                               returned by billing_account_json — but was displayed
                                               nowhere, so "why is this account frozen?" had no answer
                                               in the UI. The badge tooltip is the natural home for it. */
                                            <span
                                                title={
                                                    account.archive_reason
                                                        ? `Archived ${account.archived_at} — ${account.archive_reason}`
                                                        : `Archived ${account.archived_at}`
                                                }
                                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                                            >
                                                Archived
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right text-sm">
                                    <div className="flex items-center justify-end gap-3">
                                        <span className="text-indigo-600 font-medium">
                                            {selectedAccount?.id === account.id ? 'Close' : 'Manage'}
                                        </span>
                                        {account.archived_at ? (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleRestore(account); }}
                                                className="font-medium text-foreground hover:opacity-80"
                                            >
                                                Restore
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setArchiveTarget(account); }}
                                                className="font-medium text-muted-foreground hover:text-foreground"
                                            >
                                                Archive account
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(account); }}
                                            className="font-medium text-destructive hover:text-destructive/80"
                                        >
                                            Delete account
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Credit Management + Transactions Panel */}
            {selectedAccount && (
                <div ref={panelRef} className="space-y-6 scroll-mt-4">
                    <div className="bg-card rounded-lg border border-border p-4">
                        <h2 className="text-lg font-semibold text-foreground mb-4">
                            Manage Credits — {selectedAccount.owner.name}
                            <span className="ml-2 text-sm font-normal text-muted-foreground">Balance: {Number(selectedAccount.total_balance ?? selectedAccount.balance ?? 0).toFixed(2)}</span>
                        </h2>

                        {msg.text && (
                            <div className={`mb-4 rounded-lg px-4 py-2.5 text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                {msg.text}
                            </div>
                        )}

                        <form onSubmit={handleCreditAction} className="flex items-end gap-3">
                            <div className="w-36">
                                <label className="block text-sm font-medium text-foreground mb-1">Action</label>
                                <select value={creditAction} onChange={(e) => setCreditAction(e.target.value)} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm">
                                    <option value="add">Add Credits</option>
                                    <option value="remove">Remove Credits</option>
                                </select>
                            </div>
                            <div className="w-36">
                                <label className="block text-sm font-medium text-foreground mb-1">Amount</label>
                                <input type="number" step="0.01" min="0.01" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" placeholder="0.00" required />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                                <input type="text" value={creditDescription} onChange={(e) => setCreditDescription(e.target.value)} placeholder="Reason for credit adjustment" className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" />
                            </div>
                            <button type="submit" disabled={submitting} className={`px-5 py-2 text-sm font-medium rounded-md text-white disabled:opacity-50 ${creditAction === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                                {submitting ? 'Processing...' : creditAction === 'add' ? 'Add Credits' : 'Remove Credits'}
                            </button>
                        </form>
                    </div>

                    {/* Subscription Management */}
                    <AdminSubscriptionPanel billingAccountId={selectedAccount.id} onChange={fetchAccounts} />

                    {/* Per-workspace archive/restore. Keyed on the account id so switching
                        accounts mounts a fresh instance (fresh state) instead of reusing one
                        whose in-flight request/rows belong to the previously selected account. */}
                    <AdminWorkspacesPanel key={selectedAccount.id} account={selectedAccount} />

                    {/* Per-account plan limit overrides */}
                    <AdminLimitOverridesPanel account={selectedAccount} onChange={fetchAccounts} />

                    {/* Recent Transactions */}
                    <div className="bg-card rounded-lg border border-border">
                        <div className="px-4 py-3 border-b border-border">
                            <h3 className="text-sm font-semibold text-foreground">Recent Transactions</h3>
                        </div>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-muted/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timestamp</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Component</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Description</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {txnLoading ? (
                                    <tr><td colSpan={5} className="px-4 py-3 text-center text-muted-foreground">Loading...</td></tr>
                                ) : transactions.length === 0 ? (
                                    <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">No transactions yet</td></tr>
                                ) : transactions.map(t => (
                                    <tr key={t.id}>
                                        <td className="px-6 py-3 text-sm text-muted-foreground">{new Date(t.created_at).toLocaleString()}</td>
                                        <td className="px-6 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.transaction_type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {t.transaction_type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-sm text-muted-foreground capitalize">{t.component.replace(/_/g, ' ')}</td>
                                        <td className="px-6 py-3 text-sm text-foreground">{t.description}</td>
                                        <td className="px-6 py-3 text-right text-sm font-medium">
                                            <span className={t.transaction_type === 'debit' ? 'text-red-600' : 'text-green-600'}>
                                                {t.transaction_type === 'debit' ? '-' : '+'}{Number(t.amount).toFixed(2)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <DeleteAccountModal
                    account={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onDeleted={() => {
                        setDeleteTarget(null);
                        if (selectedAccount?.id === deleteTarget.id) setSelectedAccount(null);
                        fetchAccounts();
                    }}
                />
            )}

            {archiveTarget && (
                <ArchiveAccountModal
                    account={archiveTarget}
                    onClose={() => setArchiveTarget(null)}
                    onArchived={() => {
                        setArchiveTarget(null);
                        // Same reasoning as handleRestore/DeleteAccountModal's onDeleted:
                        // archiving drops the account out of the default (non-archived)
                        // view, so leaving it selected would keep the panel open on a
                        // row that has just disappeared from the table.
                        if (selectedAccount?.id === archiveTarget.id) setSelectedAccount(null);
                        fetchAccounts();
                    }}
                />
            )}
        </div>
    );
};

export default AdminBilling;
